/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import { ModelOutput, IModelOutputBaseConfig } from "../ModelOutput";
import { ModelInput } from "../ModelInput";
import { timeoutPromise } from "../../util";

/**
 * API output configuration
 */
export interface IOutputAPIConfig<ValueType> extends IModelOutputBaseConfig {
	/** API endpoint URL */
	url: string;

	/** Function to return request body parameters */
	bodyFn: (inputs: { [K: string]: ModelInput<any, any, any> }) => { [K: string]: any };

	/** Function to return output value from API response */
	outputFn: (response: any) => ValueType;

	/** Synthetic delay (to prevent too much calls on input changes) */
	delay?: number;
}

/**
 * API output
 */
export class ModelOutputAPI<ValueType> extends ModelOutput<ValueType, IOutputAPIConfig<ValueType>> {

	/** Delay timeout ref */
	private delayTimeout: Promise<any> = null;

	protected async compute(inputs: { [K: string]: ModelInput<any, any, any> }) {

		if (this.delayTimeout) {
			await this.delayTimeout;
		}

		const bodyParams = this.config.bodyFn(inputs);

		const res = await fetch(this.config.url, {
			method: "POST",
			mode: "cors",
			cache: "no-cache",
			headers: {
				"Accept": "application/json",
				"Content-type": "application/json"
			},
			body: JSON.stringify(bodyParams)
		});

		if (!res.ok) {
			console.error("Failed to call API. Server responded with status '%d' and body:", res.status, await res.text());
			throw new Error("Failed to call API.");
		}

		let resData;

		try {
			resData = await res.json();
			console.log("API RES:", resData);
		} catch (err) {
			throw err;
		}

		if (this.config.delay) {
			this.delayTimeout = timeoutPromise(this.config.delay);
		}

		return this.config.outputFn(resData);

	}

}

/**
 * API output field
 *
 * @param opts Options
 */
export function outputAPI<ValueType>(opts: IOutputAPIConfig<ValueType>, defaultValue: ValueType) {

	return (handler) =>
		new ModelOutputAPI<ValueType>(handler, "api", defaultValue, opts);

}
